import { HanamiElement, createShadow, define } from '../core/base';
import { t } from '../core/strings';

const css = `:host{display:inline-flex}button{display:inline-grid;place-items:center;width:var(--hn-copy-size,2rem);height:var(--hn-copy-size,2rem);padding:0;border:0;border-radius:var(--hn-radius-sm);background:transparent;color:var(--hn-color-text-muted);cursor:pointer;transition:background-color var(--hn-duration-fast),color var(--hn-duration-fast)}button:hover{background:var(--hn-color-hover);color:var(--hn-color-text)}button:focus-visible{outline:none;box-shadow:var(--hn-focus-ring)}svg{grid-area:1 / 1;width:1rem;height:1rem;transition:transform var(--hn-duration-normal) var(--hn-ease-pop),opacity var(--hn-duration-fast)}.check{opacity:0;transform:scale(0.3);color:var(--hn-color-success)}:host([data-state="copied"]) .copy{opacity:0;transform:scale(0.3)}:host([data-state="copied"]) .check{opacity:1;transform:none}.live{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}`;

const html = `<button part="button" type="button">
  <svg class="copy" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
  <svg class="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
</button><span class="live" role="status"></span>`;

/**
 * Copie un texte dans le presse-papiers.
 * Attributs : value (texte) ou from="id" (contenu d'un élément), label.
 * Événement : hn-copy (detail.value) ou hn-copy-error.
 */
export class HnCopyButton extends HanamiElement {
  static readonly tagName = 'hn-copy-button';
  static get observedAttributes() {
    return ['label'];
  }

  private button!: HTMLButtonElement;
  private live!: HTMLElement;
  private reset?: ReturnType<typeof setTimeout>;

  get value(): string {
    return this.getAttribute('value') ?? '';
  }
  set value(v: string) {
    this.setAttribute('value', v);
  }

  connectedCallback(): void {
    this.upgradeProperties('value');
    if (!this.shadowRoot) {
      const root = createShadow(this, css, html);
      this.button = root.querySelector('button')!;
      this.live = root.querySelector('.live')!;
      this.button.addEventListener('click', () => void this.copy());
    }
    this.attributeChangedCallback();
  }

  attributeChangedCallback(): void {
    if (!this.button) return;
    const label = this.getAttribute('label') || t('copy');
    this.button.setAttribute('aria-label', label);
    this.button.title = label;
  }

  private text(): string {
    const from = this.getAttribute('from');
    if (from) {
      const el = (this.getRootNode() as Document | ShadowRoot).getElementById?.(from) ?? document.getElementById(from);
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el.value;
      return el?.textContent ?? '';
    }
    return this.value;
  }

  /** Copie le texte ; renvoie true en cas de succès. */
  async copy(): Promise<boolean> {
    const value = this.text();
    let ok = false;
    try {
      await navigator.clipboard.writeText(value);
      ok = true;
    } catch {
      const area = document.createElement('textarea');
      area.value = value;
      area.setAttribute('readonly', '');
      area.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
      document.body.append(area);
      area.select();
      try {
        ok = document.execCommand('copy');
      } catch {
        ok = false;
      }
      area.remove();
    }
    clearTimeout(this.reset);
    this.live.textContent = ok ? t('copied') : t('copyFailed');
    if (ok) {
      this.dataset.state = 'copied';
      this.emit('hn-copy', { value });
    } else {
      this.emit('hn-copy-error', { value });
    }
    this.reset = setTimeout(() => {
      delete this.dataset.state;
      this.live.textContent = '';
    }, 1800);
    return ok;
  }
}

define(HnCopyButton.tagName, HnCopyButton);
